import { useQuery } from '@tanstack/react-query';
import { projects } from '@/api/projects';
import type { Project, ProjectKind } from '@/api/projects';

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  organizationId?: string;
  disabled?: boolean;
  kind?: ProjectKind;
}

/** Активні напрями / проєкти організації (один ключ запиту для селектора і для поля). */
export function useActiveProjects(organizationId?: string, kind?: ProjectKind) {
  return useQuery({
    queryKey: ['projects-active', organizationId ?? '', kind ?? 'ALL'],
    // Власник 2026-06-02: запит з organization_id='' → 422.
    // Запит має сенс лише коли org обрана.
    enabled: !!organizationId,
    queryFn: () => projects.list({
      organization_id: organizationId,
      status: 'ACTIVE',
      kind,
      limit: 500,
    }),
    staleTime: 60_000,
  });
}

/**
 * Селектор напряму/проекта для редакторів документів.
 * - Без kind: дві групи (Напрямки, Проєкти) — обирається будь-яке.
 * - kind='DIRECTION': лише наскрізні напрямки.
 * - kind='PROJECT': лише разові проєкти.
 */
export default function ProjectSelect({ value, onChange, organizationId, disabled, kind }: Props) {
  const projectsQ = useActiveProjects(organizationId, kind);

  const items = projectsQ.data?.items ?? [];
  const directions = items.filter((p) => p.kind === 'DIRECTION');
  const projectsOnly = items.filter((p) => p.kind === 'PROJECT');

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
    >
      <option value="">
        {kind === 'DIRECTION' ? '— Без напряму —' : kind === 'PROJECT' ? '— Без проєкту —' : '— Без напряму/проєкту —'}
      </option>
      {kind === 'DIRECTION' || kind === undefined ? (
        <optgroup label="Напрямки (наскрізні)">
          {directions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.code}
            </option>
          ))}
        </optgroup>
      ) : null}
      {kind === 'PROJECT' || kind === undefined ? (
        <optgroup label="Проєкти (тимчасові)">
          {projectsOnly.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.code}
            </option>
          ))}
        </optgroup>
      ) : null}
    </select>
  );
}

interface FieldProps extends Props {
  /** Ширина у сітці `.doc-grid` (Закон дизайну #8а). */
  span?: number;
  label?: string;
}

/**
 * ч.1365 (рішення власника): поле «Напрям» у документі показується ЛИШЕ коли в організації є
 * хоч один активний напрям чи проєкт — або коли в документі вже стоїть значення (перенесений з
 * 1С, напрям пізніше заархівовано). Хто напрямами не користується, порожнього списку не бачить.
 */
export function ProjectField({ span = 3, label = 'Напрям', ...props }: FieldProps) {
  const projectsQ = useActiveProjects(props.organizationId, props.kind);
  const items: Project[] = projectsQ.data?.items ?? [];
  if (!props.value && items.length === 0) return null;
  return (
    <div className="field" style={{ gridColumn: `span ${span}` }}>
      <label>{label}</label>
      <ProjectSelect {...props} />
    </div>
  );
}
